import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Zap,
  PieChart,
  BarChart3,
  Settings,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../utils/cn';
import { APILogDashboard } from './APILogDashboard';
import { APIPricingDashboard } from './APIPricingDashboard';
import { useNavigate } from 'react-router-dom';

// Cost Dashboard Interfaces
interface CostMetrics {
  totalCost: number;
  averageCostPerMessage: number;
  costTrend: number;
  savingsThisMonth: number;
  budgetUtilization: number;
  topExpenseChannel: string;
}

interface BudgetStatus {
  id: string;
  budgetName: string;
  budgetLimit: number;
  currentSpend: number;
  remainingBudget: number;
  percentageUsed: number;
  status: 'active' | 'warning' | 'exceeded';
  daysRemaining: number;
  projectedSpend: number;
}

interface OptimizationRecommendation {
  id: string;
  type: 'provider_switch' | 'timing_adjustment' | 'volume_consolidation' | 'content_optimization';
  title: string;
  description: string;
  potentialSavings: number;
  implementationEffort: 'low' | 'medium' | 'high';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'implementing' | 'completed';
}

interface CostBreakdown {
  channel: string;
  cost: number;
  percentage: number;
  trend: number;
  messagesCount: number;
}

interface CostDashboardProps {
  className?: string;
}

export const CostDashboard: React.FC<CostDashboardProps> = ({ className }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30days');
  const [autoRefresh, setAutoRefresh] = useState(false);

  // State for cost data
  const [costMetrics, setCostMetrics] = useState<CostMetrics | null>(null);
  const [budgetStatuses, setBudgetStatuses] = useState<BudgetStatus[]>([]);
  const [recommendations, setRecommendations] = useState<OptimizationRecommendation[]>([]);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown[]>([]);
  const [costTrends, setCostTrends] = useState<Array<{ date: string; cost: number; savings: number }>>([]);

  // Add at the top:
  const cyclingTexts = [
    'Optimize SMS spend with AI',
    'Track multi-channel costs',
    'Maximize campaign ROI',
    'Monitor budget utilization',
    'Discover cost-saving opportunities',
    'Analyze API usage and pricing',
    'Control your outreach expenses',
    'Automate cost reporting',
  ];
  const [cyclingIndex, setCyclingIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCyclingIndex((prev) => (prev + 1) % cyclingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const navigate = useNavigate();

  const channelOptions = useMemo(() => [
    { label: 'All Channels', value: 'all' },
    { label: 'SMS', value: 'sms' },
    { label: 'Voice', value: 'voice' },
    { label: 'Email', value: 'email' },
  ], []);
  const [selectedChannel, setSelectedChannel] = useState('all');

  // Fetch cost dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Handle demo data for now
      const mockCostMetrics: CostMetrics = {
        totalCost: 2847.32,
        averageCostPerMessage: 0.0285,
        costTrend: -8.5,
        savingsThisMonth: 342.18,
        budgetUtilization: 67.8,
        topExpenseChannel: 'SMS'
      };

      const mockBudgetStatuses: BudgetStatus[] = [
        {
          id: '1',
          budgetName: 'Q1 2024 SMS Budget',
          budgetLimit: 10000,
          currentSpend: 6780,
          remainingBudget: 3220,
          percentageUsed: 67.8,
          status: 'active',
          daysRemaining: 23,
          projectedSpend: 8950
        },
        {
          id: '2',
          budgetName: 'Monthly Voice Budget',
          budgetLimit: 5000,
          currentSpend: 4650,
          remainingBudget: 350,
          percentageUsed: 93.0,
          status: 'warning',
          daysRemaining: 5,
          projectedSpend: 5200
        }
      ];

      const mockRecommendations: OptimizationRecommendation[] = [
        {
          id: '1',
          type: 'provider_switch',
          title: 'Switch SMS Provider for Cost Savings',
          description: 'Switch from Twilio to MessageBird for SMS messages to save $0.002 per message',
          potentialSavings: 156.80,
          implementationEffort: 'medium',
          priority: 'high',
          status: 'pending'
        },
        {
          id: '2',
          type: 'timing_adjustment',
          title: 'Optimize Message Timing',
          description: 'Shift non-urgent messages to off-peak hours (6 PM - 8 AM) for 15% cost reduction',
          potentialSavings: 89.25,
          implementationEffort: 'low',
          priority: 'medium',
          status: 'pending'
        },
        {
          id: '3',
          type: 'volume_consolidation',
          title: 'Consolidate Daily Sends',
          description: 'Batch messages to achieve volume discounts on daily sends over 1000 messages',
          potentialSavings: 234.50,
          implementationEffort: 'medium',
          priority: 'high',
          status: 'implementing'
        }
      ];

      const mockCostBreakdown: CostBreakdown[] = [
        { channel: 'SMS', cost: 1847.32, percentage: 64.9, trend: -5.2, messagesCount: 64800 },
        { channel: 'Voice', cost: 750.00, percentage: 26.3, trend: 2.1, messagesCount: 7500 },
        { channel: 'Email', cost: 250.00, percentage: 8.8, trend: -12.5, messagesCount: 125000 }
      ];

      const mockCostTrends = Array.from({ length: 30 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toISOString().split('T')[0],
          cost: Math.random() * 100 + 50,
          savings: Math.random() * 20 + 5
        };
      });

      setCostMetrics(mockCostMetrics);
      setBudgetStatuses(mockBudgetStatuses);
      setRecommendations(mockRecommendations);
      setCostBreakdown(mockCostBreakdown);
      setCostTrends(mockCostTrends);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  // Auto refresh every 30 seconds if enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchDashboardData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getBudgetStatusColor = (status: string, percentageUsed: number) => {
    if (status === 'exceeded' || percentageUsed >= 100) return 'text-red-400';
    if (status === 'warning' || percentageUsed >= 80) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getRecommendationPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          className="flex items-center space-x-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
          <span className="text-lg text-gray-600 dark:text-gray-300">Loading cost analytics...</span>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      className={cn(
        'min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex',
        className
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Sidebar Filters */}
      <div className="w-72 flex-shrink-0 bg-neutral-900/80 border-r border-neutral-800/60 min-h-screen p-6 hidden md:block">
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-2 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-blue-400" /> Cost Filters
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Channel</label>
              <select
                value={selectedChannel}
                onChange={e => setSelectedChannel(e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                {channelOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Date Range</label>
              <select
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-1">Cost Type</label>
              <select
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                defaultValue="all"
              >
                <option value="all">All Types</option>
                <option value="fixed">Fixed</option>
                <option value="variable">Variable</option>
                <option value="optimization">Optimization</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <Button variant="primary" className="w-full" onClick={() => navigate('/api-logs')}>
            View API Logs
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 p-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <h1 className="text-5xl font-bold text-white mb-4">Cost Management</h1>
          <div className="text-3xl font-semibold h-12 flex items-center justify-center mb-2">
            <motion.span
              key={cyclingIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-400 bg-clip-text text-transparent"
            >
              {cyclingTexts[cyclingIndex]}
            </motion.span>
          </div>
          <p className="text-lg text-neutral-400 mb-6">Track, analyze, and optimize your spending across all channels with AI-powered insights.</p>
          <div className="max-w-2xl mx-auto mb-6">
            <input
              type="text"
              placeholder={'Search cost analytics, e.g. "Show SMS spend last month"'}
              className="w-full px-6 py-4 text-lg border border-neutral-700 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-neutral-900 text-white shadow-lg"
            />
          </div>
          <div className="flex items-center justify-center space-x-4">
            <Button variant="outline" size="sm" onClick={() => setActiveTab('overview')}>Overview</Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('budgets')}>Budgets</Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('optimization')}>Optimization</Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('roi')}>ROI</Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('api-logs')}>API Logs</Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('api-pricing')}>API Pricing</Button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        {costMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Total Cost</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {formatCurrency(costMetrics.totalCost)}
                      </p>
                      <div className="flex items-center mt-2">
                        {costMetrics.costTrend < 0 ? (
                          <TrendingDown className="h-4 w-4 text-green-400 mr-1" />
                        ) : (
                          <TrendingUp className="h-4 w-4 text-red-400 mr-1" />
                        )}
                        <span className={cn(
                          "text-sm font-medium",
                          costMetrics.costTrend < 0 ? "text-green-400" : "text-red-400"
                        )}>
                          {Math.abs(costMetrics.costTrend)}%
                        </span>
                        <span className="text-gray-400 text-sm ml-1">vs last month</span>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-500/20 rounded-full">
                      <DollarSign className="h-6 w-6 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Avg Cost/Message</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {formatCurrency(costMetrics.averageCostPerMessage)}
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        Top: {costMetrics.topExpenseChannel}
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/20 rounded-full">
                      <BarChart3 className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Savings This Month</p>
                      <p className="text-2xl font-bold text-green-400 mt-1">
                        {formatCurrency(costMetrics.savingsThisMonth)}
                      </p>
                      <p className="text-gray-400 text-sm mt-2">
                        From optimizations
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/20 rounded-full">
                      <TrendingDown className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm font-medium">Budget Utilization</p>
                      <p className="text-2xl font-bold text-white mt-1">
                        {costMetrics.budgetUtilization}%
                      </p>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div
                          className={cn(
                            "h-2 rounded-full transition-all duration-300",
                            costMetrics.budgetUtilization > 80 ? "bg-yellow-400" : "bg-blue-400"
                          )}
                          style={{ width: `${Math.min(costMetrics.budgetUtilization, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-yellow-500/20 rounded-full">
                      <Target className="h-6 w-6 text-yellow-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-gray-800/50 backdrop-blur-lg border border-gray-700">
            <TabsTrigger value="overview" className="text-white data-[state=active]:bg-blue-600">
              Overview
            </TabsTrigger>
            <TabsTrigger value="budgets" className="text-white data-[state=active]:bg-blue-600">
              Budgets
            </TabsTrigger>
            <TabsTrigger value="optimization" className="text-white data-[state=active]:bg-blue-600">
              Optimization
            </TabsTrigger>
            <TabsTrigger value="roi" className="text-white data-[state=active]:bg-blue-600">
              ROI
            </TabsTrigger>
            <TabsTrigger value="api-logs" className="text-white data-[state=active]:bg-blue-600">
              API Logs
            </TabsTrigger>
            <TabsTrigger value="api-pricing" className="text-white data-[state=active]:bg-blue-600">
              API Pricing
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost Trends Chart */}
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Cost Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <PieChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-400">Cost trends chart would be rendered here</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Showing {costTrends.length} days of cost data
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Channel Breakdown */}
              <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <PieChart className="h-5 w-5 mr-2" />
                    Channel Cost Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {costBreakdown.map((channel, index) => (
                      <motion.div
                        key={channel.channel}
                        className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 rounded-full bg-blue-400" />
                          <div>
                            <p className="text-white font-medium">{channel.channel}</p>
                            <p className="text-gray-400 text-sm">
                              {channel.messagesCount.toLocaleString()} messages
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">{formatCurrency(channel.cost)}</p>
                          <div className="flex items-center">
                            <span className="text-gray-400 text-sm mr-2">{channel.percentage}%</span>
                            {channel.trend < 0 ? (
                              <TrendingDown className="h-3 w-3 text-green-400" />
                            ) : (
                              <TrendingUp className="h-3 w-3 text-red-400" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Budgets Tab */}
          <TabsContent value="budgets" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {budgetStatuses.map((budget, index) => (
                <motion.div
                  key={budget.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white">{budget.budgetName}</CardTitle>
                        <Badge 
                          className={cn(
                            "text-xs",
                            getBudgetStatusColor(budget.status, budget.percentageUsed)
                          )}
                        >
                          {budget.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Spent</span>
                          <span className="text-white font-medium">
                            {formatCurrency(budget.currentSpend)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Budget</span>
                          <span className="text-white font-medium">
                            {formatCurrency(budget.budgetLimit)}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Utilization</span>
                            <span className="text-white">{budget.percentageUsed}%</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div
                              className={cn(
                                "h-2 rounded-full transition-all duration-300",
                                budget.percentageUsed >= 100 ? "bg-red-400" :
                                budget.percentageUsed >= 80 ? "bg-yellow-400" : "bg-blue-400"
                              )}
                              style={{ width: `${Math.min(budget.percentageUsed, 100)}%` }}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div>
                            <p className="text-gray-400 text-sm">Remaining</p>
                            <p className="text-white font-medium">
                              {formatCurrency(budget.remainingBudget)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-400 text-sm">Days Left</p>
                            <p className="text-white font-medium">{budget.daysRemaining}</p>
                          </div>
                        </div>

                        {budget.projectedSpend > budget.budgetLimit && (
                          <div className="flex items-center p-3 bg-yellow-500/20 rounded-lg">
                            <AlertTriangle className="h-4 w-4 text-yellow-400 mr-2" />
                            <span className="text-yellow-400 text-sm">
                              Projected to exceed by {formatCurrency(budget.projectedSpend - budget.budgetLimit)}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Optimization Tab */}
          <TabsContent value="optimization" className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              {recommendations.map((recommendation, index) => (
                <motion.div
                  key={recommendation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              getRecommendationPriorityColor(recommendation.priority)
                            )} />
                            <h3 className="text-white font-semibold">{recommendation.title}</h3>
                            <Badge className="text-xs">
                              {recommendation.type.replace('_', ' ')}
                            </Badge>
                          </div>
                          
                          <p className="text-gray-300 mb-4">{recommendation.description}</p>
                          
                          <div className="flex items-center space-x-6">
                            <div>
                              <p className="text-gray-400 text-sm">Potential Savings</p>
                              <p className="text-green-400 font-semibold">
                                {formatCurrency(recommendation.potentialSavings)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm">Effort</p>
                              <p className="text-white">{recommendation.implementationEffort}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm">Priority</p>
                              <p className="text-white">{recommendation.priority}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col space-y-2">
                          {recommendation.status === 'pending' && (
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                              <Zap className="h-4 w-4 mr-2" />
                              Implement
                            </Button>
                          )}
                          {recommendation.status === 'implementing' && (
                            <Button size="sm" variant="outline" disabled>
                              <Clock className="h-4 w-4 mr-2" />
                              In Progress
                            </Button>
                          )}
                          {recommendation.status === 'completed' && (
                            <Button size="sm" variant="outline" disabled>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Completed
                            </Button>
                          )}
                          <Button size="sm" variant="outline">
                            <Settings className="h-4 w-4 mr-2" />
                            Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* ROI Tab */}
          <TabsContent value="roi" className="space-y-6">
            <Card className="bg-gray-800/50 backdrop-blur-lg border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <PieChart className="h-5 w-5 mr-2" />
                  ROI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-gray-400 text-sm">ROI analysis would be rendered here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Logs Tab */}
          <TabsContent value="api-logs">
            <APILogDashboard />
          </TabsContent>

          {/* API Pricing Tab */}
          <TabsContent value="api-pricing">
            <APIPricingDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
};

export default CostDashboard; 